import React from "react";
import { graphql } from "gatsby";
import Layout from "../../components/layout/Layout";
import SEO from "@v4/gatsby-theme-talend/src/components/Seo/Seo";
import { Helmet } from "react-helmet";

import { heroDataObj } from "../data/pressReleaseListingPageData/heroProps";
import Hero from "../../components/modules/hero/Hero";
import Pagination from "../../components/pagination/Pagination";
import { pressReleasesTranslations, newsArticlesTranslations } from "../../translations/newsListing";
import { commonTranslations } from "@v4/gatsby-theme-talend/src/translations/common";

import * as newsEntryStyles from "./newsEntryListingPage.module.css";
import classNames from "classnames/bind";

import SingleNewsEntryItem from "../../components/singleNewsEntryItem/SingleNewsEntryItem";

export const query = graphql`
    query($skip: Int = 0, $perPage: Int = 5, $locale: String = "en", $languages: [String]) {
        pressReleases: allContentfulPressRelease(
            filter: { node_locale: { eq: $locale }, languages: { in: $languages } }
            sort: { fields: date, order: DESC }
            limit: $perPage
            skip: $skip
        ) {
            totalCount
            nodes {
                ...PressRelease
            }
        }

        newsArticles: allContentfulNewsArticle(
            filter: { node_locale: { eq: $locale }, languages: { in: $languages } }
            sort: { fields: date, order: DESC }
            limit: $perPage
            skip: $skip
        ) {
            totalCount
            nodes {
                ...Article
            }
        }

        contentfulMenu(name: { eq: "Main Nav" }, node_locale: { eq: $locale }) {
            ...MainMenu
        }
        footerNav: contentfulMenu(name: { eq: "Footer Nav" }, node_locale: { eq: $locale }) {
            ...FooterNav
        }
        footerSocials: contentfulMenu(name: { eq: "Footer Socials" }, node_locale: { eq: $locale }) {
            ...FooterSocials
        }
        footerSubMenu: contentfulMenu(name: { eq: "Footer Sub Menu" }, node_locale: { eq: $locale }) {
            ...FooterSubMenu
        }
    }
`;

export default function NewsEntryListing({ data, pageContext }) {
    // Defining the content nodes
    const contentData = data[pageContext.entryType].nodes;

    // Validation
    if (!contentData) return;

    // Data for use with footer components
    const footerData = {
        footerNav: data.footerNav,
        footerSocials: data.footerSocials,
        footerSubMenu: data.footerSubMenu,
    };

    // Define which translation object to use
    const translationObj =
        pageContext.entryType === "pressReleases" ? pressReleasesTranslations : newsArticlesTranslations;

    // Pagination-based suffix for title and description. ie: "Page 4"
    const pageCountSuffix = pageContext.currentPage
        ? ` – ${commonTranslations[pageContext.locale].pageStr} ${pageContext.currentPage}`
        : "";

    // Meta values for the SEO component, with some conditions
    const metaValues = {
        description: `${translationObj[pageContext.locale].metaDescription}${pageCountSuffix}`,
        title: `${translationObj[pageContext.locale].metaTitle}${pageCountSuffix}`,
        pathname: pageContext.currentPage ? pageContext.base + pageContext.currentPage : pageContext.base,
        modifiedTime: contentData.date,
        createdTime: contentData.date,
    };

    // Define the hero props
    const heroProps = heroDataObj(
        translationObj[pageContext.locale].heroHeading,
        translationObj[pageContext.locale].heroSubHeading
    );

    // Ensure the raw content for the Heading fields is parsed into JSON (only once)
    if (typeof heroProps.heroHeading.raw !== "string")
        heroProps.heroHeading.raw = JSON.stringify(heroProps.heroHeading.raw);
    if (typeof heroProps.heroSubHeading.raw !== "string")
        heroProps.heroSubHeading.raw = JSON.stringify(heroProps.heroSubHeading.raw);

    // Set up style props, CX binding method
    const styleProps = {};
    const cx = classNames.bind(newsEntryStyles);

    return (
        <Layout
            headerMenu={data.contentfulMenu}
            footerMenu={footerData}
            notificationBar={data.notificationBar}
            pageStyleProps={styleProps}
        >
            <SEO
                title={metaValues.title}
                pathname={metaValues.pathname}
                description={metaValues.metaDescription}
                modifiedTime={metaValues.updatedAt}
                createdTime={metaValues.createdAt}
                languages={["All"]}
            />
            <Helmet bodyAttributes={{ class: "darkOpaque" }} />
            <Hero {...heroProps} />
            <section className={cx("container", "newsEntryWrapper")}>
                {contentData.map((entry, index) => (
                    <SingleNewsEntryItem key={`news-item--${index}`} {...entry} />
                ))}
                <Pagination
                    perPage={pageContext.perPage}
                    totalCount={data[`${pageContext.entryType}`].totalCount}
                    currentPage={pageContext.currentPage + 1}
                    skip={pageContext.skip}
                    base={pageContext.base}
                />
            </section>
        </Layout>
    );
}
